import { Injectable } from '@angular/core';
import { LoggingService } from '@logging/logging.service';
import { OnScreenMessagingService } from '@pattern-library/onscreen-messaging/onscreen-messaging.service';
import { Observable, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ErrorHandlerService {

  constructor(
    private messaging: OnScreenMessagingService,
    private logger: LoggingService
  ) {}


  /**
   * Handles errors from GET/fetch operations
   * @param err The error object
   * @param options Options for error handling
   * @param storeName Name of the store for logging purposes
   * @returns Observable that throws the error after handling
   */
  handleGetErrors(err: any, options: { suppressToast?: boolean } = {}, storeName: string): Observable<never> {
    this.logger.error(`${storeName}: Fetch failed`, err);
    
    if (err.status === 404) {
      if (!options.suppressToast) { 
        this.messaging.warn('The requested record could not be found.');
      }
    } else if (err.status === 400) {
      if (!options.suppressToast) {
        this.messaging.warn('Bad request - please check your input.');
      }
    } else {
      if (!options.suppressToast) {
        this.messaging.warn(`An error occurred while fetching the record for ${storeName}.`);
      }
    }
    return throwError(() => err);
  }

  /**
   * Handles errors from POST/PUT/DELETE operations
   * @param err The error object
   * @param creating Whether this is a create operation (true) or update (false)
   * @param storeName Name of the store for logging purposes
   * @param options Options for error handling
   * @returns Observable that throws the error after handling
   */
  handlePersistError(err: any, creating: boolean, storeName: string, options: { suppressToast?: boolean } = {}): Observable<never> {
    this.logger.error(`${storeName}: ${creating ? 'Create' : 'Update'} failed`, err);
    
    if (err.status === 400) {
      if (!options.suppressToast) {
        this.messaging.warn('There was an error while persisting the record.');
      }
    } else if (err.status === 404) {
      if (!options.suppressToast) {
        this.messaging.warn('The record could not be found.');
      }
    } else if (err.status === 403) {
      if (!options.suppressToast) {
        this.messaging.warn('You do not have permission to perform this action.');
      }
    } else {
      if (!options.suppressToast) {
        this.messaging.warn(`An error occurred while ${creating ? 'creating' : 'updating'} the record for ${storeName}.`);
      }
    }
    return throwError(() => err);
  }

  /**
   * Handles errors from DELETE operations
   * @param err The error object
   * @param storeName Name of the store for logging purposes
   * @param options Options for error handling
   * @returns Observable that throws the error after handling
   */
  handleDeleteError(err: any, storeName: string, options: { suppressToast?: boolean } = {}): Observable<never> {
    this.logger.error(`${storeName}: Delete failed`, err);
    
    if (err.status === 400) {
      if (!options.suppressToast) {
        this.messaging.warn('The request to the server failed.');
      }
    } else if (err.status === 404) {
      if (!options.suppressToast) {
        this.messaging.warn('The resource could not be found.');
      }
    } else if (err.status === 403) {
      if (!options.suppressToast) {
        this.messaging.warn('You do not have permission to perform this action.');
      }
    } else {
      if (!options.suppressToast) {
        this.messaging.warn('An error occurred while performing the delete operation.');
      }
    }
    return throwError(() => err);
  }

  /**
   * Extracts server validation errors from an error response.
   * Supports: `errors` map, `detail` (problem details), or a JSON array body such as
   * `[{ message: string, statusCode?: string }, ...]` (joined into `detail`).
   */
  extractValidationErrors(err: any): Record<string, string> | undefined {
    if (err?.status !== 400 || err?.error == null) {
      return undefined;
    }

    const body = err.error;

    if (body.errors && typeof body.errors === 'object' && !Array.isArray(body.errors)) {
      const mapped = mapRecordValidationErrors(body.errors);
      if (Object.keys(mapped).length > 0) {
        return mapped;
      }
    }

    if (Array.isArray(body.validationErrors) && body.validationErrors.length > 0) {
      const mapped = mapValidationErrorItems(body.validationErrors);
      if (Object.keys(mapped).length > 0) {
        return mapped;
      }
    }

    if (Array.isArray(body)) {
      const messages = body
        .map((entry: { message?: string }) => entry.message)
        .filter((message: string | undefined): message is string => Boolean(message));
      if (messages.length > 0) {
        return { detail: messages.join(', ') };
      }
    }

    if (typeof body.message === 'string' && body.message) {
      return { detail: body.message };
    }

    if (typeof body.detail === 'string' && body.detail) {
      return { detail: body.detail };
    }

    return undefined;
  }
}

function mapRecordValidationErrors(errors: Record<string, unknown>): Record<string, string> {
  const mapped: Record<string, string> = {};

  for (const [key, value] of Object.entries(errors)) {
    if (typeof value === 'string' && value) {
      mapped[key] = value;
      continue;
    }

    if (Array.isArray(value) && value.length > 0) {
      mapped[key] = value.map(String).join(', ');
    }
  }

  return mapped;
}

function mapValidationErrorItems(
  items: { propertyId?: string; errors?: string[] }[]
): Record<string, string> {
  const mapped: Record<string, string> = {};

  for (const item of items) {
    if (!item.propertyId || !item.errors?.length) {
      continue;
    }

    mapped[item.propertyId] = item.errors.join(', ');
  }

  return mapped;
}
